import React from 'react';

/**
 * Select Component - Touch-friendly dropdown with consistent styling
 * 
 * @param {Object} props
 * @param {string} props.label - Select label text
 * @param {string} props.error - Error message to display
 * @param {Array} props.options - Array of {value, label} objects
 * @param {string} props.placeholder - Placeholder text for default option
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.required - Mark as required
 * @param {*} props...rest - Additional select props
 */
const Select = React.forwardRef(({
    label,
    error,
    options = [],
    placeholder = 'Select an option',
    className = '',
    required = false,
    ...props
}, ref) => {
    const selectClassName = `
    w-full px-4 py-3 text-base
    border-2 rounded-lg
    min-h-touch
    bg-white dark:bg-navy-800 text-gray-900 dark:text-gray-100
    transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent
    disabled:bg-gray-100 dark:disabled:bg-navy-800 disabled:cursor-not-allowed
    ${error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500'}
    ${className}
  `;

    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <select
                ref={ref}
                className={selectClassName}
                required={required}
                {...props}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
});

Select.displayName = 'Select';

export default Select;
